import React, { useState, useMemo } from 'react';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import JoinEventDialog from '../../dialogs/JoinEventDialog';
import { formatPlannedRoundLabel } from '../../../common/plannedRoundCount';
import { shortenDescription } from '../../../common/description';
import { setTopJoinConfirmation } from '../../../common/topJoinConfirmation';

const EventItem = ({ event, masterTags, updateMasterTags, useDialog, user }) => {

  const [isDialogOpen, setDialogOpen] = useState(false);

  const participantsCount = Number(event.participants_count || 0);
  const shouldShowParticipantsMeta = participantsCount > 0;
  const isCompletedEvent = Boolean(event.completion_status && event.completion_status !== 'active');
  const actionLabel = isCompletedEvent ? '結果を見る' : useDialog ? '参加する' : 'ルームへ戻る';
  const plannedRoundLabel = formatPlannedRoundLabel(event.planned_round_count);

  const tagLabels = useMemo(() => {
    if (event.tags && masterTags){
      try {
        if (masterTags.length === 0) {
          console.warn('マスタ未所持のためマスタ取得を要求');
          if (updateMasterTags) updateMasterTags();
          return [];
        }
        const tagIds = event.tags;
        if (!Array.isArray(tagIds)) return [];
  
        const labels = tagIds.map(id => {
          const match = masterTags.find(tag => tag.id === id);
          return match ? match.name : null;
        });
  
        if (labels.includes(null)) {
          console.warn('不明なタグIDが含まれているためマスタ再取得を要求');
          if (updateMasterTags) updateMasterTags();
          return [];
        }
  
        return labels;
      } catch (e) {
        console.error('タグの解析中にエラー', e);
        return [];
      }
    } 
  }, [event.tags, masterTags, updateMasterTags]);

  const handleClick = () => {
    if (useDialog) {
      setDialogOpen(true);
    } else {
      window.location.href = `/room?id=${event.room_id}`;
    }
  };

  return (
    <>
      <Card
        style={{
          maxWidth: '400px',
          minWidth: '300px',
          minHeight: event.participants ? '240px' : '220px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          margin: '16px 4px',
          boxSizing: 'border-box',
          display: 'flex',
          flex: '1 1 300px',
          backgroundColor: '#f9f9f9',
        }}
      >
        <CardActionArea
          onClick={handleClick}
          sx={{
            height: '100%',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              backgroundColor: '#f2f2f2',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            },
            '&:active': {
              backgroundColor: '#e8f0ff',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)'
            },
            '& .MuiCardActionArea-focusHighlight': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <div
            style={{
              minHeight: event.participants ? '240px' : '220px',
              padding: '16px',
              boxSizing: 'border-box',
              display: 'flex',
              flex: '1 1 300px',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '8px',
              alignItems: 'center'
            }}>
              {event.completion_status === 'active' && (
                <Chip label="開催中" size="small" color="success" />
              )}
              {event.completion_status && event.completion_status !== 'active' && (
                <Chip label="終了" size="small" color="default" />
              )}
              {shouldShowParticipantsMeta && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  color: '#666',
                  fontSize: '12px',
                  fontWeight: 700,
                  lineHeight: 1
                }}>
                  <PersonOutlineIcon sx={{ fontSize: 16, color: '#777' }} />
                  {participantsCount}人参加中
                </span>
              )}
            </div>

            {event.participants && event.participants.length === 1 && (
              <div style={{
                fontSize: '13px',
                color: '#0066cc',
                backgroundColor: '#e6f2ff',
                padding: '4px 8px',
                borderRadius: '6px',
                marginBottom: '8px',
                fontWeight: 'bold',
                alignSelf: 'flex-start'
              }}>
                {event.participants[0].name} さんが参加中！
              </div>
            )}

            {event.participants && event.participants.length > 1 && (
              <div style={{
                fontSize: '13px',
                color: '#0066cc',
                backgroundColor: '#e6f2ff',
                padding: '4px 8px',
                borderRadius: '6px',
                marginBottom: '8px',
                fontWeight: 'bold',
                alignSelf: 'flex-start'
              }}>
                {event.participants[0].name} さん達が参加中！
              </div>
            )}
            
            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
              {event.name}
            </div>
            {/* タグを文字列で出す場合 */}
            {/* <div style={{ fontSize: '14px', color: '#555', marginBottom: '8px' }}>
              タグ: {tagLabels && tagLabels.length > 0 ? tagLabels.join(', ') : 'なし'}
            </div> */}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              { tagLabels && tagLabels.map((label, idx) => (
                <Chip key={idx} label={label} size="small" color="primary" />
              ))}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              ラウンド: {event.round} ラウンド目
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              {plannedRoundLabel}
            </div>
            <div style={{ fontSize: '12px', color: '#555', display: 'flex', justifyContent: 'space-between' }}>
              <span>必要参加チケット: {event.allowed_entry_tickets}枚</span>
            </div>

            <div style={{ fontSize: '14px', color: '#555', marginBottom: '8px' }}>
              {shortenDescription(event.description, 30)}
            </div>  

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              marginTop: '8px'
            }}>
              { event.owner_name && (
                <div style={{ fontSize: '12px', color: '#888' }}>
                  作成者: {event.owner_name}
                </div>
              )}
              <span style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '76px',
                padding: '6px 12px',
                borderRadius: '4px',
                backgroundColor: '#1976d2',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 'bold',
                lineHeight: 1
              }}>
                {actionLabel}
              </span>
            </div>
          </div>
        </CardActionArea>
      </Card>

      <JoinEventDialog
        isVisible={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        event={event}
        tagLabels={tagLabels || []}
        user={user}
        onJoin={() => {
          setTopJoinConfirmation(event.room_id);
          window.location.href = `/room?id=${event.room_id}`;
        }}
      />
    </>
  )
};

export default EventItem;
